import {v4 as uuidv4} from 'uuid';
import kebabCase from 'lodash/kebabCase';
import Autorun from './snippets/Autorun';
import Button from './snippets/Button';
import FormGroup from './snippets/FormGroup';
import Image from './snippets/Image';
import Input from './snippets/Input';
import Select from './snippets/Select';
import Text from './snippets/Text';

/*
# Notes
https://github.com/kraaden/autocomplete
https://getbootstrap.com/docs/4.4/components/list-group/
https://micromodal.now.sh/
 */

const ENUMS = {
    REF_ATTR: 'snippetManagerReference'
};

const blockOverrides = {
    Carousel: (data, proc) => {
        data.items = data.items.map(
            item => { item.content = proc(item.content); return item; }
        );
        return data;
    },
    SplashScreen: (data, proc) => {
        data.items = data.items.map(
            item => { item.text = proc(item.text); return item; }
        );
        return data;
    },
    Result: (data, proc) => {
        data.items = data.items.map(
            item => { item.text = proc(item.text); return item; }
        );
        return data;
    },
    TextColumns: (data, proc) => {
        data.items = data.items.map(
            item => { item.text = proc(item.text); return item; }
        );
        return data;
    }
};

const snippetLibrary = {
    input: Input,
    button: Button,
    select: Select,
    autorun: Autorun,
    formGroup: FormGroup,
    text: Text,
    image: Image,
};

const snippetReferences = {};

const snippetLibrarian = function(input, event, container) {

    // Get the string without the surrounding square brackets
    const match = input.match(/\[\[([^\[\]]*)\]\]/);

    // If the input isn't surrounded with double square brackets, skip this
    if (!match) {
        return false;
    }

    // Get the snippet information
    const snippet = match[1].split('|', 2);

    // Get the snippet name (as written in the snippetLibrary keys)
    const type = snippet[0];

    // If the snippet library is missing this snippet. The replacer uses double
    // square brackets as well, so this will be required at some points.
    if (!Object.keys(snippetLibrary).includes(type)) {
        return false;
    }

    // Convert the data string to a data object
    let data = {};

    if (snippet[1]) {
        const dataEntries = snippet[1]
            .split(';')
            .map(function(pair) {
                return pair.split('=');
            });
        // data = Object.fromEntries(dataEntries);
        data = Array.from(dataEntries).reduce((acc, [ key, val ]) => Object.assign(acc, { [key]: val }), {});
    }

    if (data.label) {
        data.label = data.label.replace(new RegExp(/{(.+)}/), (match, key) => {
            return LanguageManager.getString(key)
                ? LanguageManager.getString(key) : match;
        });
    }

    if (data.text) {
        data.text = data.text.replace(new RegExp(/{(.+)}/), (match, key) => {
            const replacements = {
                name: () => {
                    if(container.componentList.FirebaseStorage.data()) {
                        return [
                            container.componentList.FirebaseStorage.data().firstName,
                            container.componentList.FirebaseStorage.data().lastName,
                        ].filter(Boolean).join(' ');
                    }
                },
                email: () => { 
                    if(container.componentList.FirebaseStorage.data()) {
                        return container.componentList.FirebaseStorage.data().email;
                    }
                    // container.componentList.FirebaseStorage.data()?.email
                }
            };

            if (!replacements[key] && LanguageManager.getString(key)) {
                return LanguageManager.getString(key);
            }

            if (!replacements[key]) {
                return match;
            }

            return replacements[key]();
        });
    }

    /** @var {HTMLElement} element */
    const element = snippetLibrary[type].render(data, event, container);

    if (!element) {
        return false;
    }

    const ref = uuidv4();
    element.dataset[ENUMS.REF_ATTR] = ref;

    // Store a reference to this element for later
    snippetReferences[ref] = {type, data};

    return element;
};

const SnippetManager = Component.extend({
    name: 'SnippetManager',
    container: null,

    setup: function(container) {
        this.container = container;
        this.listenTo(
            container.screenManager,
            'onBlockInitialized',
            this.renderSnippets
        );
        this.listenTo(
            container.screenManager,
            'onBlockRendered',
            this.postRenderSnippets
        );
    },

    /**
     * Add the HTML for the new snippet to the page
     * @param event
     */
    renderSnippets(event) {
        const {view} = event;
        const template = view.blockData.attributes.template;

        // Skip if we don't know how to handle this template type
        // eg Carousel, ImageList, SplashScreen
        if (!Object.keys(blockOverrides).includes(template)) {
            return;
        }

        // Update the data with the snippets
        view.data = blockOverrides[template](view.data, input => {
            const regex = /(\[\[[^\[\]]*\]\])/;
            return input.split(new RegExp(regex, 'g')).map(s => {
                // Get the compiled snippet from the librarian
                const snippet = snippetLibrarian(s, event, this.container);

                // If the librarian couldn't create a snippet, return the
                // original text
                return snippet ? snippet.outerHTML : s;
            }).join(''); // join's default is ',' but we don't want commas
        });
    },

    /**
     * Apply any events or changes to the new snippet element that is now on the
     * page
     * @param event
     */
    postRenderSnippets(event) {
        const kebabAttr = kebabCase(ENUMS.REF_ATTR);
        Logger.info('snippetReferences', snippetReferences);
        Object.entries(snippetReferences).forEach(([id, {type, data}]) => {
            const element = document.querySelector(`[data-${kebabAttr}="${id}"]`);

            if (!element) {
                return;
            }

            Logger.info('POST RENDERING SNIPPET', {
                element,
                kebabAttr: kebabAttr,
                id,
                type,
                data,
                event,
                selector: `[data-${kebabAttr}="${id}"]`,
            });

            if (snippetLibrary[type].postRender) {
                snippetLibrary[type].postRender(
                    element,
                    type,
                    data,
                    this.container,
                    event
                );
            }
            delete snippetReferences[id];
        });
    }
});

window.SnippetManager = SnippetManager;
